import * as React from 'react';

import { StatusSourceSummary } from '../status-source-summary';

import { cn } from '@/lib/utils';
import { formatDistance } from '@/lib/formatDate';

export interface ReviewListItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Unique identifier for the knowledge article */
  id: string;
  /** Primary text displayed in the first row */
  title: string;
  /** Content rendered in the top-right corner (e.g. relative timestamp) */
  timestamp: Date;
  /** Icon rendered at the start of the second row */
  icon?: React.ReactNode;
  /** Text or content next to the icon in the second row */
  description?: string | null;
  /** Whether this item is new */
  isNew?: boolean;
  /** Whether this item has been accepted (e.g. accepted as draft) */
  isAccepted?: boolean;
  /** Number of edits for this item */
  editCount?: number;
  /** Whether this item is currently selected */
  isSelected?: boolean;
}

export const ReviewListItem = React.forwardRef<HTMLButtonElement, ReviewListItemProps>(function ReviewListItem(
  { title, timestamp, icon, description, isNew, isAccepted, editCount, isSelected = false, className, ...props },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type="button"
      aria-pressed={isSelected}
      className={cn(
        'relative flex w-full flex-col gap-1 bg-surface rounded p-4 cursor-pointer transition-colors text-left border border-transparent',
        isSelected
          ? [
              'shadow-[0px_1px_3px_0px_rgba(95,95,95,0.26)]',
              'after:content-[""] after:block after:absolute after:right-[-2px] after:top-1/2 after:-translate-y-1/2 after:w-1 after:h-14 after:rounded-sm after:bg-primary',
            ]
          : 'hover:bg-dark-100 hover:border-dark-300',
        className,
      )}
    >
      {/* Row 1: Title + Timestamp */}
      <span className="flex items-center justify-between w-full gap-2">
        <span className={cn('text-sm text-default truncate min-w-0 flex-1', isSelected ? 'font-bold' : 'font-normal')}>
          {title}
        </span>
        {timestamp && <span className="text-xs leading-4 text-subtlest shrink-0">{formatDistance(timestamp)}</span>}
      </span>

      {/* Row 2: Status + Icon + Description */}
      <StatusSourceSummary
        isNew={isNew}
        isAccepted={isAccepted}
        editCount={editCount}
        sourceIcon={icon}
        sourceDescription={description}
      />
    </button>
  );
});
